import { formatAddress, formatDate, titleCase } from '../BusinessDetailReport/businessReportModel'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const yes = (value) => value === true || /^(Y|YES|TRUE|1)$/.test(upper(value))
const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const percent = (score) => `${Math.round(score * 100)}%`
const listName = (value) => titleCase(value).replace(/\b(Ofac|Fbi|Osfi|Un|Pep)\b/g, w => w.toUpperCase())

export const scoreOf = (hit) => {
    const score = Number(hit?.MaxMatchScore) || 0
    return score > 1 ? score / 100 : score
}
export const strengthOf = (hit) => {
    const score = scoreOf(hit)
    return score >= 0.999 ? 'exact' : score >= 0.9 ? 'strong' : 'possible'
}
export const isOfac = (hit) => /OFAC|FOREIGN ASSET/.test(upper(hit?.DataSource))
const isCompany = (hit) => upper(hit?.RecordType) === 'N'

const BIRTH_LABEL = /\bDOB\b|\bDATE OF BIRTH\b|\bBIRTH ?DATE\b|\bBORN\b/i
const birthYears = (remarks) => [...new Set(remarks.flatMap(r => text(r).split(/[;|]/))
    .filter(part => BIRTH_LABEL.test(part))
    .flatMap(part => (part.slice(part.search(BIRTH_LABEL)).split(/\b(?:POB|PLACE OF BIRTH|NATIONALITY|CITIZEN|PASSPORT|ADDRESS|LISTED|ADDED|REGISTERED)\b/i)[0]
        .match(/\b(19|20)\d{2}\b/g) || []).map(Number)))]

const STRENGTH = {
    exact: { label: 'Exact match', tone: 'declined', rank: 3 },
    strong: { label: 'Strong match', tone: 'declined', rank: 2 },
    possible: { label: 'Possible match', tone: 'neutral', rank: 1 },
}

export const buildWatchlistHit = (hit, owner = {}) => {
    const score = scoreOf(hit)
    const company = isCompany(hit)
    const strength = company ? 'possible' : strengthOf(hit)
    const remarks = list(hit?.Remarks?.Remark).map(text).filter(Boolean)
    const years = birthYears(remarks)
    const ownerYear = Number(owner?.DOB?.Year) || null
    const yearCheck = !ownerYear || !years.length || company ? null : years.includes(ownerYear)
    const akas = list(hit?.AKAs?.AKA).filter(a => a?.PartyName)
    const addresses = list(hit?.Addresses?.Address).map(a => [...list(a?.AddressLines?.Line).map(titleCase), titleCase(a?.Country)].filter(Boolean).join(', ')).filter(Boolean)
    const countries = [...new Set([titleCase(hit?.BlockedCountry), ...list(hit?.Addresses?.Address).map(a => titleCase(a?.Country))].filter(Boolean))]

    return {
        strength, serious: strength !== 'possible', ofac: isOfac(hit), company, score,
        rank: STRENGTH[strength].rank,
        title: nameCase(hit?.PartyName) || 'Name not given',
        facts: [listName(hit?.DataSource), company ? 'Company' : 'Person', countries.join(', ')].filter(Boolean).join(' · '),
        chips: [
            isOfac(hit) && { label: 'OFAC', tone: strength !== 'possible' ? 'declined' : 'watch' },
            { label: `${STRENGTH[strength].label}${score > 0 ? ` ${percent(score)}` : ''}`, tone: STRENGTH[strength].tone },
            company && { label: 'Company, not a person', tone: 'neutral' },
            yearCheck === false && { label: `Birth year differs (${years.join(', ')})`, tone: 'neutral' },
            yearCheck === true && { label: 'Birth year matches', tone: 'declined' },
        ].filter(Boolean),
        details: [
            ['Same person?', company ? 'This list record is a company, not a person'
                : yearCheck === true ? `The list gives birth year ${years.join(', ')}, the same as the owner`
                    : yearCheck === false ? `The list gives birth year ${years.join(', ')}; the owner was born in ${ownerYear}`
                        : years.length && !ownerYear ? `The list gives birth year ${years.join(', ')}; the owner's date of birth is not on file`
                            : 'Lexis matched this record by name; the list gives no birth date to compare'],
            ['List', listName(hit?.DataSource)],
            ['Match score', score > 0 ? `${percent(score)} (${STRENGTH[strength].label.toLowerCase()})` : 'No score given'],
            ['Record type', company ? 'Company or organization' : upper(hit?.RecordType) === 'I' ? 'Person' : text(hit?.RecordType)],
            ['Blocked country', titleCase(hit?.BlockedCountry)],
            ['Also known as', akas.map(a => `${nameCase(a.PartyName)}${Number(a.MatchScore) > 0 ? ` (${percent(Number(a.MatchScore) > 1 ? a.MatchScore / 100 : Number(a.MatchScore))})` : ''}`).join('; ')],
            ['Addresses', addresses.join('; ')],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        blocks: remarks.length ? [{ title: 'Remarks from the list', details: remarks.map((r, n) => ({ label: remarks.length > 1 ? `${n + 1}` : 'Remark', value: r })) }] : [],
    }
}

export function buildWatchlists(hits, owner = {}) {
    const rows = list(hits).filter(h => h && typeof h === 'object').map(h => buildWatchlistHit(h, owner))
        .sort((a, b) => b.rank - a.rank || b.score - a.score)
    const strong = rows.filter(r => r.serious)
    const possible = rows.filter(r => !r.serious)
    return {
        rows, strong, possible,
        summary: {
            count: rows.length,
            exact: rows.filter(r => r.strength === 'exact').length,
            strong: rows.filter(r => r.strength === 'strong').length,
            possible: possible.length,
            ofac: rows.filter(r => r.ofac).length,
            ofacSerious: strong.filter(r => r.ofac).length,
        },
    }
}

const SERIOUS = /REVOK|REVOCATION|EXCLU|SUSPEN|SURRENDER|DEBAR|LOSS OF LICENSE/
const ACTIONS = [[/REVOK|REVOCATION|LOSS OF LICENSE/, 'Revoked'], [/EXCLU/, 'Excluded'], [/SUSPEN/, 'Suspended'], [/SURRENDER/, 'Surrendered'], [/DEBAR/, 'Debarred']]

const sameDob = (a, b) => {
    if (!a?.Year || !b?.Year) return null
    if (Number(a.Year) !== Number(b.Year)) return 'different'
    if (a.Month && b.Month && Number(a.Month) !== Number(b.Month)) return 'different'
    if (a.Day && b.Day && Number(a.Day) !== Number(b.Day)) return 'different'
    return a.Month && b.Month && a.Day && b.Day ? 'same' : 'year'
}
const money = (value) => {
    const number = Number(text(value).replace(/[^0-9.]/g, ''))
    return number > 0 ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace(/\.00$/, '') : ''
}

export const buildSanction = (s, owner = {}, now = new Date()) => {
    const action = [upper(s?.Type), upper(s?.Condition)].join(' ')
    const lostLicense = yes(s?.LostOfLicenseIndicator)
    const serious = SERIOUS.test(action) || lostLicense
    const fraud = yes(s?.FraudAbuseFlag)
    const dob = sameDob(s?.DOB, owner?.DOB)
    const name = nameCase([s?.Name?.First, s?.Name?.Middle, s?.Name?.Last, s?.Name?.Suffix].filter(Boolean).join(' '))
    const sanctioned = dateValue(s?.SanctionDate)
    const years = sanctioned ? (now.getTime() - sanctioned) / (365.25 * 86400000) : null
    const board = upper(s?.BoardType)
    const type = titleCase(s?.Type) || 'Board action'
    const condition = titleCase(s?.Condition)

    return {
        serious, sanctionedAt: sanctioned,
        title: [type, condition && condition.toUpperCase() !== type.toUpperCase() && condition].filter(Boolean).join(' · '),
        facts: [listName(s?.Source), s?.State, s?.SanctionDate && `Sanctioned ${formatDate(s.SanctionDate)}`, titleCase(s?.ProviderType)].filter(Boolean).join(' · '),
        chips: [
            ...ACTIONS.filter(([pattern]) => pattern.test(action)).map(([, label]) => ({ label, tone: 'declined' })),
            lostLicense && { label: 'Lost license', tone: 'declined' },
            fraud && { label: 'Fraud or abuse', tone: 'declined' },
            /^(FEDERAL|STATE)$/.test(board) && { label: board === 'FEDERAL' ? 'Federal' : 'State board', tone: 'neutral' },
            years != null && years > 10 && { label: `${Math.floor(years)} years ago`, tone: 'neutral' },
            dob === 'different' && { label: 'Date of birth differs', tone: 'watch' },
        ].filter(Boolean),
        details: [
            ['Same person?', dob === 'same' ? 'Date of birth matches the owner' : dob === 'year' ? 'Birth year matches the owner (no full date to compare)' : dob === 'different' ? 'Date of birth differs from the owner'
                : 'Lexis matched this action by name; no date of birth to compare'],
            ['Action', [type, condition].filter((v, i, all) => v && all.findIndex(x => x.toUpperCase() === v.toUpperCase()) === i).join(' · ')],
            ['Reason', titleCase(s?.Reason)],
            ['Terms', text(s?.Terms)],
            ['Fines', money(s?.Fines)],
            ['Lost license', lostLicense ? 'Yes' : upper(s?.LostOfLicenseIndicator) === 'N' ? 'No' : ''],
            ['Fraud or abuse', fraud ? 'Yes' : ''],
            ['Sanctioned', formatDate(s?.SanctionDate)],
            ['Updated', formatDate(s?.UpdateDate)],
            ['Issued by', [listName(s?.Source), board && `${titleCase(board)} board`, s?.State].filter(Boolean).join(' · ')],
            ['Provider type', titleCase(s?.ProviderType)],
            ['License number', text(s?.SanctionLicenseNumber)],
            ['Name on record', name],
            ['Date of birth on record', formatDate(s?.DOB)],
            ['Address on record', formatAddress(s?.Address)],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
    }
}

export function buildSanctions(sanctions, owner = {}, now = new Date()) {
    const rows = list(sanctions).filter(s => s && typeof s === 'object').map(s => buildSanction(s, owner, now))
        .sort((a, b) => b.serious - a.serious || b.sanctionedAt - a.sanctionedAt)
    const latest = Math.max(0, ...rows.map(r => r.sanctionedAt))
    return {
        rows,
        summary: {
            count: rows.length,
            serious: rows.filter(r => r.serious).length,
            latestYear: latest ? new Date(latest).getFullYear() : null,
        },
    }
}
